"use client";

import * as RadixDialog from "@radix-ui/react-dialog";
import React, { ReactNode, useRef } from "react";
import "./Dialog.scss";

export type BasicDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: ReactNode;
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  className?: string;
  titleTooltip?: string;
};

export function Dialog({
  open,
  onOpenChange,
  title,
  children,
  initialFocusRef,
  returnFocusRef,
  className,
  titleTooltip,
}: BasicDialogProps) {
  // Track the input modality of the dismissal so focus is returned to the trigger
  // for keyboard dismissals (ESC, or keyboard-activated ✕) but not for pointer
  // dismissals (click outside, or a mouse click on ✕), a mouse user does not
  // expect a focus ring to jump onto the trigger. Defaults to "keyboard" so any
  // path we can't classify still restores focus (the accessible default).
  const closeReasonRef = useRef<"keyboard" | "pointer">("keyboard");

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="dialogOverlay" />
        <RadixDialog.Content
          className={`dialogContent${className ? ` ${className}` : ""}`}
          aria-describedby={undefined}
          onEscapeKeyDown={() => {
            closeReasonRef.current = "keyboard";
          }}
          onPointerDownOutside={() => {
            closeReasonRef.current = "pointer";
          }}
          onOpenAutoFocus={(event) => {
            if (!initialFocusRef?.current) return;
            event.preventDefault();
            initialFocusRef.current.focus();
          }}
          onCloseAutoFocus={(event) => {
            const reason = closeReasonRef.current;
            closeReasonRef.current = "keyboard";
            if (!returnFocusRef?.current) return;
            // Keyboard dismissals (ESC, or keyboard-activated ✕) return focus to
            // the trigger. For pointer dismissals (click outside / mouse click on
            // ✕) prevent the default return-to-trigger and let focus fall to
            // <body>, a mouse user does not expect a focus ring on the trigger.
            event.preventDefault();
            if (reason !== "pointer") {
              returnFocusRef.current.focus();
            }
          }}
        >
          <RadixDialog.Title className="dialogTitle" title={titleTooltip}>
            {title}
          </RadixDialog.Title>
          {children}
          <RadixDialog.Close asChild>
            <button
              type="button"
              className="dialogClose"
              aria-label="Close"
              onClick={(event) => {
                // detail === 0 → activated via keyboard (Enter/Space); a real
                // mouse click reports detail > 0.
                closeReasonRef.current =
                  event.detail === 0 ? "keyboard" : "pointer";
              }}
            >
              ×
            </button>
          </RadixDialog.Close>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
